import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import { LoadingButton } from '@mui/lab';
import { GridColDef, DataGridPro } from '@mui/x-data-grid-pro';
import { Box, Grid, IconButton, Tab, Tabs, Theme, Toolbar, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useI18n } from '../../../../../application/language/useI18n';
import { ContainerContext } from '../../../../../common/container/ContainerContext';
import { ConstructorType, injectValue } from '../../../../../common/container/inject';
import { observer } from 'mobx-react';
import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Colors } from '../../../../design/color/Colors';
import { ListItemLink, ListItemLinkColor } from '../../../../design/list/ListItemLink';
import { ProjectButtonLink } from '../../../../design/link/ProjectButtonLink';
import { PageEmptyContent } from '../../../../design/page/PageEmptyContent';
import { PageLoader } from '../../../../design/page/PageLoader';
import { IUserPageProperties } from '../../IUserPageProperties';
import { AllApplicationsPageHeader } from '../info/parts/ApplicationPageHeader';
import { ProjectInfoButtonInject } from './inject/ProjectInfoButtonInject';
import { InvitationLinksWrapper } from './parts/InvitationLinksWrapper';
import { ProjectApplicationsTable } from './parts/ProjectApplicationsTable';
import { ProjectInfoHeader } from './parts/ProjectInfoHeader';
import { ProjectTeamTable } from './parts/ProjectTeamTable';
import { TransferModalWindow } from './parts/TransferModalWindow';
import { TransferApplicationType } from './parts/TransferTypeEnum';
import { IProjectApplicationView } from './store/model/IProjectApplicationView';
import { IProjectView } from './store/model/IProjectView';
import { ProjectInfoDomain } from './store/ProjectInfoDomain';

export interface IProjectInfoProperties extends IUserPageProperties {}

type ProjectInfoTab = 'systems' | 'team';

export const ProjectInfo = observer(({ layoutDomain }: IProjectInfoProperties) => {
  const { translate } = useI18n();
  const [pageDomain] = useState<ProjectInfoDomain>(
    new (injectValue<ConstructorType<ProjectInfoDomain>>(ProjectInfoDomain))(layoutDomain),
  );
  const { projectId = undefined } = useParams<any>();
  const classes = useStyles();
  const currentProjects =
    pageDomain.ui.filteredProjects.list.length || pageDomain.ui.searchTerm.value
      ? pageDomain.ui.filteredProjects.list
      : pageDomain.ui.projects.list;
  const isCanLoadMoreProjects = !(pageDomain.ui.projectListRenderLimit.value >= currentProjects.length);

  useEffect(() => {
    if (projectId) {
      return;
    }

    const projectListLazyLoad = [
      {
        handler: async () => {
          pageDomain.ui.projectListRenderLimit.setValue(pageDomain.ui.projectListRenderLimit.value + 15);
        },
        idName: `load-more-application-button`,
        isOnce: true,
      },
    ];
    layoutDomain.ui.addDetections(projectListLazyLoad);

    return () => {
      layoutDomain.ui.removeDetections(projectListLazyLoad);
    };
  }, [layoutDomain, pageDomain, pageDomain.ui.projectListRenderLimit.value, projectId]);

  useEffect(() => {
    pageDomain.loadData(projectId);
    if (projectId) {
      pageDomain.loadDependecies(projectId as string);
    }
  }, [pageDomain, projectId]);

  useEffect(() => {
    if (projectId && pageDomain.ui.projects.list[0]?.name) {
      layoutDomain.setPageTitle(`${translate('projectPage.project')}: ${pageDomain.ui.projects.list[0].name}`);
    }
  }, [layoutDomain, projectId, pageDomain.ui.projects.list, translate]);

  const projects = pageDomain.ui.projects;
  const isLoading = pageDomain.ui.isLoading.value;
  const isEmptyContent = currentProjects.length === 0;
  const emptyContentMessage = projectId
    ? translate('projectPage.projectIsEmpty')
    : pageDomain.ui.searchTerm
    ? translate('projectPage.noResults')
    : translate('projectPage.noProjects');

  return (
    <>
      {!projectId && <AllApplicationsPageHeader projectInfoDomain={pageDomain} />}
      <Grid
        className={projectId ? `${classes.mainContainer} ${classes.projectInfoPageContainer}` : classes.mainContainer}
        container
        spacing={2}
        direction="column"
        justifyContent="center"
        alignItems="flex-start"
      >
        <TransferModalWindow domain={pageDomain} />
        {isLoading ? (
          <PageLoader />
        ) : isEmptyContent ? (
          <PageEmptyContent text={emptyContentMessage} isFullPage={false} />
        ) : projects?.list[0]?.id ? (
          <>
            {projectId ? (
              <Grid item className={`${classes.mainContainer} ${classes.compactContainer}`}>
                <ProjectInfoCompactView domain={pageDomain} project={projects.list[0]} />
              </Grid>
            ) : (
              <>
                {currentProjects.map((project, index) => {
                  if (index < pageDomain.ui.projectListRenderLimit.value) {
                    return (
                      <Fragment key={project.id}>
                        <Grid item marginTop={5}>
                          <ProjectView domain={pageDomain} project={project} />
                        </Grid>
                        <Toolbar />
                      </Fragment>
                    );
                  }
                  return null;
                })}
                {isCanLoadMoreProjects && (
                  <LoadingButton
                    id={`load-more-application-button`}
                    onClick={() => pageDomain.ui.projectListRenderLimit.setValue(pageDomain.ui.projectListRenderLimit.value + 10)}
                    variant="outlined"
                    color="primary"
                    style={{ marginTop: '10px', margin: '0 20px 0 16px' }}
                  >
                    {translate('projectPage.loadMore')}
                  </LoadingButton>
                )}
                <Toolbar />
              </>
            )}
          </>
        ) : (
          <Grid item className={classes.mainContainer}>
            <Grid container direction="row">
              <PageEmptyContent text={translate('projectPage.youAreNotAparticipantInTheProjects')} isFullPage={false} />
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
});

const ProjectInfoCompactView = observer(({ project, domain }: { project: IProjectView; domain: ProjectInfoDomain }) => {
  const { translate } = useI18n();
  const classes = useStyles();
  const container = useContext(ContainerContext);
  const projectInfoButtonInject = container.get(ProjectInfoButtonInject);
  const [activeTab, setActiveTab] = useState<ProjectInfoTab>('systems');
  const isCanTransferApplication = domain.isUserCanTransferApplication();
  const editUrl = project.isUnitedWithApplication
    ? `/project/edit/${project.id}/application/${project.unitedApplicationId}`
    : `/project/${project.id}/edit`;

  const openTransferModalWindow = useCallback(
    (application: IProjectApplicationView, transferType: TransferApplicationType) => {
      domain.ui.transferApplicationType.setValue(transferType);
      domain.ui.transferApplication.setEntity(application);
      domain.ui.isOpenTransferModal.setValue(true);
    },
    [domain],
  );

  const showActionsColumn = isCanTransferApplication && project.applications.some(app => !app.isUnitedWithProject);

  const columns = useMemo<GridColDef[]>(() => {
    const cols: GridColDef[] = [
      {
        field: 'name',
        headerName: translate('projectPage.system'),
        flex: 1,
        minWidth: 340,
        sortable: false,
        renderCell: (params) => {
          const application = params.row.application as IProjectApplicationView;
          return (
            <ListItemLink
              color={ListItemLinkColor.light}
              primaryText={application.name}
              url={`/application/${application.id}/info`}
              primaryTextClassName={classes.systemLink}
              containerClassName={classes.systemLinkContainer}
            />
          );
        },
      },
    ];

    if (showActionsColumn) {
      cols.push({
        field: 'actions',
        headerName: translate('phrases.actions'),
        minWidth: 140,
        maxWidth: 160,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const application = params.row.application as IProjectApplicationView;
          if (application.isUnitedWithProject) {
            return null;
          }

          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={translate('projectPage.moveSystem')}>
                <IconButton
                  size="small"
                  onClick={() => openTransferModalWindow(application, TransferApplicationType.Transfer)}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={translate('projectPage.copySystem')}>
                <IconButton
                  size="small"
                  onClick={() => openTransferModalWindow(application, TransferApplicationType.Copy)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      });
    }

    return cols;
  }, [classes.systemLink, classes.systemLinkContainer, showActionsColumn, openTransferModalWindow, translate]);

  const rows = useMemo(
    () =>
      project.applications.map((application) => ({
        id: application.id,
        name: application.name,
        application,
      })),
    [project.applications],
  );

  return (
    <Box className={classes.compactRoot}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
        <Box>
          <Typography fontSize={22}>{`${translate('projectPage.project')}: ${project.name}`}</Typography>
          <Typography color={'textSecondary'} variant="h6">
            {project.description}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {project.activeUserCanAddApplications && !project.isUnitedWithApplication && (
            <ProjectButtonLink icon={<AddIcon />} url={`/project/${project.id}/application/new`} dataCy="add-system-button">
              {translate('projectPage.addSystem')}
            </ProjectButtonLink>
          )}
          {project.activeUserCanEditProject && (
            <ProjectButtonLink icon={<EditIcon />} url={editUrl} dataCy="edit-project-button">
              {translate('projectPage.editProject')}
            </ProjectButtonLink>
          )}
          {projectInfoButtonInject.map((ButtonComponent: any, index: number) => (
            <ButtonComponent key={index + ButtonComponent} domain={domain} layoutDomain={domain.layoutDomain} projectId={project.id} />
          ))}
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, value: ProjectInfoTab) => setActiveTab(value)}
        sx={{ marginTop: 1, borderBottom: `1px solid ${Colors.border}` }}
      >
        <Tab value="systems" label={translate('phrases.systems')} sx={{ textTransform: 'none' }} />
        <Tab value="team" label={translate('projectPage.team')} sx={{ textTransform: 'none' }} />
      </Tabs>

      <Box className={classes.tabsContent}>
        {activeTab === 'systems' && (
          <Box className={`${classes.tabPane} ${classes.systemsTabPane}`}>
            {rows.length === 0 ? (
              <PageEmptyContent text={translate('projectPage.noEstablishedSystems')} isFullPage={false} />
            ) : (
              <DataGridPro
                rows={rows}
                columns={columns}
                disableSelectionOnClick
                disableColumnMenu
                hideFooter
                rowHeight={64}
                headerHeight={48}
                className={classes.systemsGrid}
                sx={{ flex: 1, minHeight: 0 }}
              />
            )}
          </Box>
        )}

        {activeTab === 'team' && (
          <Box className={classes.tabPane}>
            <InvitationLinksWrapper project={project} domain={domain} />
            <Box className={classes.teamTableScrollArea}>
              <ProjectTeamTable
                isEmptyApplications={project.applications.length === 0}
                domain={domain}
                projectId={project.id}
                team={project.team}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
});

const ProjectView = observer(({ project, domain }: { project: IProjectView; domain: ProjectInfoDomain }) => {
  const { translate } = useI18n();
  const container = useContext(ContainerContext);
  const projectInfoButtonInject = container.get(ProjectInfoButtonInject);
  const isEmptyApplications = project.applications.length === 0;
  const isEmptyTeam = project.team.length === 0;
  const classes = useStyles();
  const editUrl = project.isUnitedWithApplication
    ? `/project/edit/${project.id}/application/${project.unitedApplicationId}`
    : `/project/${project.id}/edit`;

  return (
    <Grid item className={classes.mainContainer}>
      <Grid container direction="row">
        <Grid item={true} xs={12}>
          <ProjectInfoHeader project={project} />
        </Grid>
        <Grid item={true} xs={12}>
          {!isEmptyApplications && <Toolbar />}
          <Grid container direction="row" spacing={2}>
            {project.activeUserCanAddApplications && !project.isUnitedWithApplication && (
              <Grid item>
                <ProjectButtonLink
                  icon={<AddIcon />}
                  url={`/project/${project.id}/application/new`}
                  dataCy="add-system-button"
                >
                  {translate('projectPage.addSystem')}
                </ProjectButtonLink>
              </Grid>
            )}
            {project.activeUserCanEditProject && (
              <Grid item>
                <ProjectButtonLink icon={<EditIcon />} url={editUrl} dataCy="edit-project-button">
                  {translate('projectPage.editProject')}
                </ProjectButtonLink>
              </Grid>
            )}
            {projectInfoButtonInject.map((ButtonComponent: any, index: number) => {
              return (
                <Grid item key={index + ButtonComponent}>
                  <ButtonComponent domain={domain} layoutDomain={domain.layoutDomain} projectId={project.id} />
                </Grid>
              );
            })}
          </Grid>

          {!isEmptyApplications && (
            <ProjectApplicationsTable domain={domain} project={project} applications={project.applications} />
          )}

          <Box marginTop={2}>
            <InvitationLinksWrapper project={project} domain={domain} />
          </Box>

          {isEmptyApplications && isEmptyTeam && (
            <PageEmptyContent text={translate('projectPage.noEstablishedSystems')} isFullPage={false} />
          )}
        </Grid>

        <Grid item={true} xs={12}>
          <Toolbar />
          <ProjectTeamTable
            isEmptyApplications={isEmptyApplications}
            domain={domain}
            projectId={project.id}
            team={project.team}
          />
        </Grid>
      </Grid>
    </Grid>
  );
});

const useStyles = makeStyles((theme: Theme) => ({
  mainContainer: {
    width: '100%',
  },
  projectInfoPageContainer: {
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  compactContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  compactRoot: {
    width: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  tabsContent: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    backgroundColor: Colors.bgElevated,
  },
  tabPane: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    paddingTop: theme.spacing(2),
  },
  systemsTabPane: {
    overflow: 'hidden',
    paddingBottom: theme.spacing(2),
  },
  teamTableScrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  systemLink: {
    fontSize: '16px',
    lineHeight: '20px',
  },
  systemLinkContainer: {
    margin: 0,
    '&.MuiListItem-button:hover': {
      backgroundColor: 'transparent',
    },
  },
  systemsGrid: {
    border: 'none',
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-cell:hover': {
      backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: `1px solid ${Colors.border}`,
    },
  },
  root: {
    padding: theme.spacing(2),
  },
  hidden: {
    display: 'none',
  },
}));
